import type { KnowledgeThreadMessage } from '@/lib/api'
import { asAnalystCharts, asAnalystTables } from '@/components/quick-study-results/featuredPlots/ThreadFeaturedExhibits'
import { partitionAssistantCitations } from './partitionAssistantCitations'
import { activityLegsFromToolCalls } from './applyActivityFrame'

/**
 * A persisted message as this page holds it.
 *
 * The partition lives HERE as well as on the stream path, and that is the whole
 * point: `KnowledgeThreadMessage.citations` is the mixed
 * `KnowledgeAskCitation | AssistantCitation` array the server writes into one
 * jsonb column, so a thread that renders correctly during a session would break
 * on the next reload if reload were the one reader that skipped the split.
 *
 * Empty halves stay `undefined` rather than becoming `[]`, so a legacy grounded
 * message reloads byte-identically to how it did before web citations existed.
 */
export function messageToTurn(message: KnowledgeThreadMessage) {
  const { documentCitations, webCitations, v2 } = partitionAssistantCitations(
    message.citations,
    message,
  )

  return {
    id: message.token,
    role: message.role === 'assistant' ? 'assistant' as const : 'user' as const,
    text: message.text,
    askId: message.askId,
    createdAt: message.createdAt,
    refused: message.refused,
    gaps: message.gaps ?? undefined,
    citations: documentCitations.length > 0 ? documentCitations : undefined,
    webCitations: webCitations.length > 0 ? webCitations : undefined,
    ...(v2 ? { citationV2: v2 } : {}),
    ...(message.calculations?.length ? { calculations: message.calculations } : {}),
    ...(message.further_reading?.length ? { furtherReading: message.further_reading } : {}),
    // Agent C: rehydrate plan from the proposing message when cardEligible.
    plan:
      message.plan && message.plan.cardEligible && message.plan.steps.length >= 2
        ? message.plan
        : undefined,
    quickStudyFindings: message.quickStudyFindings ?? undefined,
    charts: asAnalystCharts(message.charts),
    tables: asAnalystTables(message.tables),
    creativeDrafts:
      message.creativeDrafts && message.creativeDrafts.length > 0
        ? message.creativeDrafts
        : undefined,
    socialTrackerProposal: message.socialTrackerProposal ?? undefined,
    // The launch card links rooms to this message, so a reloaded card needs its saved token too.
    ...(message.preparedConversation
      ? { preparedConversation: message.preparedConversation, serverToken: message.token }
      : {}),
    imageRequests: message.imageRequests ?? undefined,
    ...(message.imageApprovalRequestId ? { imageApprovalRequestId: message.imageApprovalRequestId } : {}),
    ...(message.imageApprovalFormatId ? { imageApprovalFormatId: message.imageApprovalFormatId } : {}),
    // A12: the inline report card, rebuilt from the announcing message.
    ...(message.report?.publicId ? { report: message.report } : {}),
    ...(message.reportBrief ? { reportBrief: message.reportBrief } : {}),
    // Saved canvas pictures render on reload with no extra request; pending ones keep their glow.
    ...(message.cardImages ? { cardImages: message.cardImages } : {}),
    ...(message.cardImagesPending !== undefined ? { sourceImagesLoading: message.cardImagesPending } : {}),
    activityLegs:
      message.role === 'assistant'
        ? activityLegsFromToolCalls(message.toolCalls)
        : undefined,
  }
}
